import Cookies from 'js-cookie';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CouponCookieData,
  COUPON_COOKIE_NAME,
  PARTNER_COOKIE_NAME,
  removeCookie,
  shouldClearCouponCookieOnFailure,
} from '../lib/util';
import { cartActions as storeCartActions } from '../reducers/storeCart';
import { useCartMode } from '../lib/cartModes';
import { codeResultFailureType, isValidCodeResult } from '../lib/promoCode';
import * as events from '../lib/analytics/events';
import useApplyPromotionFromCookie from './useApplyPromotionFromCookie';
import setCouponCookie, { setGwaEntitlementCookie } from '../lib/setCouponCookie';
import setPartnerCookie from '../lib/setPartnerCookie';
import { applyPartnerCodeToCartWithPrefixRetry, getUrlPartnerCode } from '../lib/giftWithPurchasePromo';
import { useLocation } from 'react-router';
import { webApiClient } from '../lib/fi-api/apiUtils';

export default function useAutoApplyCoupon() {
  const location = useLocation();
  const reduxDispatch = useDispatch();
  const { cartSelector } = useCartMode();
  const liveCart = useSelector(cartSelector);
  // Keep a live ref so the async token exchange below applies against the current cart.
  const cartRef = useRef(liveCart);
  cartRef.current = liveCart;
  const exchangedTokenRef = useRef<string | null>(null);
  const urlPartnerCode = getUrlPartnerCode();

  useEffect(() => {
    // Don't auto-apply on the redeem and vip pages because those pages have their own coupon application logic.
    if (urlPartnerCode && !location.pathname.includes('redeem') && !location.pathname.includes('vip')) {
      setCouponCookie(urlPartnerCode);
      setPartnerCookie(true);
    }
  }, [urlPartnerCode, location.pathname]);

  const fiCouponToken = new URLSearchParams(location.search).get('fi_c');
  useEffect(() => {
    if (!fiCouponToken || location.pathname.includes('redeem') || location.pathname.includes('vip')) {
      return;
    }
    // Run the exchange at most once per token.
    if (exchangedTokenRef.current === fiCouponToken) {
      return;
    }
    exchangedTokenRef.current = fiCouponToken;

    // The fitracking shop can't read the .tryfi.com coupon cookie cross-site, so resolve the signed
    // token server-side and apply the coupon directly — the cookie auto-apply below reads once on
    // mount, before this async resolves.
    (async () => {
      try {
        const { data } = await webApiClient.get<{
          code?: string;
          source?: string;
          rescueGwaProgramId?: number;
          entitlement?: string;
        }>('/api/ecommerce/resolve_coupon_token', { params: { fi_c: fiCouponToken } });
        if (!data?.code) {
          return;
        }
        // Seed the coupon cookie so `source` is forwarded and the coupon survives reloads; store the signed
        // entitlement separately so it's forwarded on pricing/validate/purchase.
        setCouponCookie(data.code, data.source ?? undefined, data.rescueGwaProgramId ?? undefined);
        if (data.entitlement) {
          setGwaEntitlementCookie(data.entitlement);
        }
        setPartnerCookie(true);

        const currentCart = cartRef.current;
        if (currentCart.couponCode !== data.code) {
          const { result, codeToApply } = await applyPartnerCodeToCartWithPrefixRetry(currentCart, data.code);
          if (isValidCodeResult(result)) {
            reduxDispatch(storeCartActions.addCoupon(codeToApply));
            events.cartPage.couponAutoApplied(codeToApply, data.source ?? undefined);
            // Keep the cookie's code aligned with what was applied so `gwaSource` is forwarded at purchase.
            if (codeToApply !== data.code) {
              setCouponCookie(codeToApply, data.source ?? undefined, data.rescueGwaProgramId ?? undefined);
            }
          } else {
            events.cartPage.couponError(codeToApply, codeResultFailureType(result));
          }
        }
      } catch {
        /* invalid or expired token — leave the cart untouched */
      }
    })();
  }, [fiCouponToken, location.pathname, reduxDispatch]);

  // The GWA coupon resolves to an *internal* coupon, which can't be validated against the empty cart
  // on the rescue landing page — so neither the exchange above nor the one-shot cookie reader below
  // applies it there. Re-attempt once the cart has an eligible item. `removeCoupon` clears the coupon
  // cookie, so this never re-adds a coupon the shopper deliberately removed.
  const cartItemCount = Object.keys(liveCart.cartItems ?? {}).length;
  useEffect(() => {
    if (cartItemCount === 0) {
      return;
    }
    const cookie = Cookies.getJSON(COUPON_COOKIE_NAME) as CouponCookieData | undefined;
    const code = cookie?.code;
    const currentCart = cartRef.current;
    if (!code || currentCart.couponCode === code) {
      return;
    }
    (async () => {
      const { result, codeToApply } = await applyPartnerCodeToCartWithPrefixRetry(currentCart, code);
      if (isValidCodeResult(result)) {
        reduxDispatch(storeCartActions.addCoupon(codeToApply));
        events.cartPage.couponAutoApplied(codeToApply, cookie?.source ?? undefined);
        if (codeToApply !== code) {
          setCouponCookie(codeToApply, cookie?.source ?? undefined, cookie?.rescueGwaProgramId ?? undefined);
        }
      } else {
        events.cartPage.couponError(codeToApply, codeResultFailureType(result));
      }
    })();
  }, [cartItemCount, reduxDispatch]);

  const [bannerText, setBannerText] = useState<string | undefined>(undefined);
  const { finished } = useApplyPromotionFromCookie<CouponCookieData>(
    useMemo(
      () => ({
        getCookieValue: () => {
          const dataValue = Cookies.getJSON(COUPON_COOKIE_NAME);
          if (dataValue) {
            return [
              dataValue,
              () => {
                /* Noop */
              },
            ];
          }

          return undefined;
        },

        applyPromotion: async ({ value, dispatch, cart }) => {
          if (cart.couponCode === value.code) {
            // If the coupon code is already applied, we don't need to apply it again.
            if (value.bannerText && value.bannerText !== bannerText) {
              setBannerText(value.bannerText);
            }
            return;
          }

          const { result, codeToApply } = await applyPartnerCodeToCartWithPrefixRetry(cart, value.code);

          // When auto applying coupon, we only don't want to do it if the coupon is not a valid code.
          // If it's other errors (e.g. "Not applicable to any items in cart") then we still want to apply it.
          if (isValidCodeResult(result)) {
            dispatch(storeCartActions.addCoupon(codeToApply));
            events.cartPage.couponAutoApplied(codeToApply, value.source);
            if (value.bannerText && value.bannerText !== bannerText) {
              setBannerText(value.bannerText);
            }

            // If we used a different code, update the coupon cookie
            if (codeToApply !== value.code) {
              setCouponCookie(codeToApply);
            }
          } else {
            events.cartPage.couponError(codeToApply, codeResultFailureType(result));
            // `removeCookie` rather than `Cookies.remove`: the coupon cookie is written against the
            // shared parent domain, and a removal that omits it silently does nothing.
            if (shouldClearCouponCookieOnFailure(Object.keys(cart.cartItems ?? {}).length)) {
              removeCookie(COUPON_COOKIE_NAME);
              removeCookie(PARTNER_COOKIE_NAME);
            }
          }
        },
      }),
      [bannerText],
    ),
  );

  return { bannerText, finished };
}
